import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import { fetchWithAuth } from "../../Functions";
import house from "../../assets/images/house.svg";
import reviews from "../../assets/images/review-icon.svg";
import team from "../../assets/images/team.svg";

const DashboardHome = () => {
    const API = process.env.REACT_APP_API_BASE_URL;
    const [analytics, setAnalytics] = useState(null);
    const [contact, setContact] = useState(null);
    const [formState, setFormState] = useState(null);
    const [isLoading, setIsLoading] = useState({
        analytics: false,
        contact: false,
        contactFormAction: false,
    });
    const navigate = useNavigate();
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });
    const [editFeedback, setEditFeedback] = useState({ success: null, message: "" });
    const [editMode, setEditMode] = useState(false);

    const fetchDashboardAnalytics = useCallback(async () => {
        try {
            setIsLoading((prev) => ({ ...prev, analytics: true }));
            const response = await fetchWithAuth(`${API}/count/`);
            const data = await response.json();

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFetchFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            if (response.ok) {
                setAnalytics(data);
                setFetchFeedback({ success: true, message: ""});
            } else {
                setFetchFeedback({ success: false, message: "Unable to load site analytics, please try again."});
                console.error(data);
            }
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error occured fetching site analytics."});
            console.error(error);
        } finally {
            setIsLoading({ analytics: false, contact: false });
        }
    }, [API, navigate]);

    const fetchContact = useCallback(async () => {
        setIsLoading((prev) => ({ ...prev, contact: true }));

        try {
            const response = await fetchWithAuth(`${API}/contact/`);

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFetchFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful responses
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error fetching contact details: ${response.status} ${response.statusText}`;
                throw new Error(errorMessage);  // Error needs to be thrown here so formState can be updated correctly in try-block 129
            }

            const data = await response.json();
            if (data?.Contact?.length) {
                const contactData = data.Contact[0];
                setContact(contactData);
                setContactInForm(contactData);
            } else {
                // No contact exists, create one
                setContactInForm({});
                setEditMode(true); // Enable form for "Save contact"
            }

            setFetchFeedback({ success: true, message: ""});
        } catch (error) {
            setFetchFeedback({ success: false, message: `An error has occured: ${error.message}`});  // Set user friendly message later
        } finally {
            setIsLoading((prev) => ({ ...prev, contact: false }));
        }
    }, [API, navigate]);

    const addContact = async (data) => {
        try {
            const response = await fetchWithAuth(`${API}/contact/create/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            // Handle unsuccessful API response
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error adding contact: ${response.status} ${response.statusText}`;
                throw new Error(errorMessage); // Error needs to be thrown here so formState can be updated correctly in try-block 129
            }

            setEditFeedback({ success: true, message: "Contact details added." });
            console.log("Contact creation successful.");
        } catch (error) {
            throw error;  // Error needs to be thrown here so formState can be updated correctly in try-block 129
        }
    }

    const updateContact = async (data) => {
        // Make API call
        try {
            const response = await fetchWithAuth(`${API}/contact/update/${contact.id}/`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            })

            // Handle unsuccessful response
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error updating contact details: ${response.status} ${response.statusText}`;
                throw new Error(errorMessage);
            }
            console.log("Contact update successful.");
        } catch (error) {
            throw error; // Error needs to be thrown here so formState can be updated correctly in try-block 129
        }
    }

    const setContactInForm = (contactData) => {
        if (contactData) {
            const formData = {
                ...contactData,
                ...contactData?.social_links,
            }
            delete formData.social_links;
            setFormState(formData);
            // console.log(contactData);
            // console.log(formData);
        }
        else {
            setFormState({});
        }
    }

    // On page load
    useEffect(() => {
        fetchDashboardAnalytics();
        fetchContact();
    }, [fetchDashboardAnalytics, fetchContact]);

    // Loads the appropriate button into the page
    const getActionButton = () => {
        if (contact) {
            if (!editMode) {
                return (
                    <Button
                        variant="warning"
                        onClick={(e) => {
                            e.preventDefault(); // Prevents form submission, allowing for button to enable form for edit only
                            setEditMode(true)}
                        }
                        className="px-5 mb-5"
                    >
                        Edit
                    </Button>
                )
            }
            return (
                <Button
                    variant="primary"
                    type="submit" // Ensures form's onSubmit handler is called
                    className="px-5 mb-5"
                >
                    Save
                </Button>
            )
        }

        // Handles when no contact exists in database
        return (
            <Button
                variant="success"
                type="submit"
                className="px-5 mb-5"
            >
                Add Contact
            </Button>
        )
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading((prev) => ({ ...prev, contactFormAction: true }));

        // Build data to be sent with API
        const payload = {
            ...formState,
            social_links: {
                instagram: formState.instagram,
                facebook: formState.facebook,
                youtube: formState.youtube,
                twitter: formState.twitter,
                linkedin: formState.linkedin,
            },
        };
        // Delete flattened social links
        delete payload.instagram;
        delete payload.facebook;
        delete payload.youtube;
        delete payload.twitter;
        delete payload.linkedin;

        // Check if changes were detected before calling API
        if (JSON.stringify(payload) === JSON.stringify(contact)) {
            setEditFeedback({ success: false, message: "No changes detected." });
            setEditMode(false);
            setIsLoading((prev) => ({ ...prev, contact: false }));
            return;
        }

        // Update contact state (only after successful update)
        try {
            if (contact) { // If contact exists, update existing one
                await updateContact(payload);
                setContact({ ...payload }); // Update local contact state to form state since update was successful
                // Form state need not be updated because the form already has updated data
                setEditFeedback({ success: true, message: "Contact details updated." });
            } else { // If no contact exists, create a new one
                await addContact(payload);
                setContact({ ...payload });
                // Load newly added contact into form
                await fetchContact();
                setContactInForm(contact);
                setEditFeedback({ success: true, message: "Contact details added." });
            }
        } catch (error) {
            // Revert formState to previous state since update was unsuccessful
            console.error(error);
            setContactInForm(contact);
            setEditFeedback({ success: false, message: `An unexpected error has occured: ${error.message}` });
        } finally {
            setIsLoading((prev) => ({ ...prev, contactFormAction: false }));
            setEditMode(false);
            // Clear feedback messages after 7 seconds
            if (editFeedback.status) {
                setTimeout(() => setEditFeedback({ status: null, message: "" }), 7000);
            }
        }
    }

    return (
        <>
        <h1 className="my-lg-5">Welcome <b className="text-secondary">Admin</b></h1>

        {/* Fetch Alert */}
        { fetchFeedback.success === false && // Display this alert only when fetchListings fails
            <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
        }

        {/* Analytics */}
        <Row xs={2} md={3} className="analytics g-4 my-2 my-md-4">
            {isLoading.analytics ? (
                <div className="d-flex flex-column align-items-center justify-content-center w-100 mt-5">
                    <Spinner animation="border" variant="primary" className="mx-auto" />
                    <p className="mt-2 text-center">Loading analytics...</p>
                </div>
            ) : (
                analytics &&
                    <>
                    {/* Property count */}
                    <Link to="/dashboard/manage-listings" className="text-decoration-none">
                        <Col>
                            <Card className="text-center shadow-sm border-0">
                                <Card.Body>
                                    <Card.Img
                                        src={ house }
                                        alt="Listings"
                                        className="mb-3"
                                    />
                                    <Card.Title className="fs-1 text-primary">{ analytics.properties }</Card.Title>
                                    <Card.Text className="text-secondary fw-semibold">Listings</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Link>
                    {/* Team count */}
                    <Link to="/dashboard/manage-team" className="text-decoration-none">
                        <Col>
                            <Card className="text-center shadow-sm border-0">
                                <Card.Body>
                                    <Card.Img
                                        src={ team }
                                        alt="Team members"
                                        className="mb-3"
                                    />
                                    <Card.Title className="fs-1 text-primary">{ analytics.team_members }</Card.Title>
                                    <Card.Text className="text-secondary fw-semibold">Team members</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Link>
                    {/* Review count */}
                    <Link to="/dashboard/manage-reviews" className="text-decoration-none">
                        <Col>
                            <Card className="text-center shadow-sm border-0">
                                <Card.Body>
                                    <Card.Img
                                        src={ reviews }
                                        alt="Reviews"
                                        className="mb-3"
                                    />
                                    <Card.Title className="fs-1 text-primary">{ analytics?.reviews }</Card.Title>
                                    <Card.Text className="text-secondary fw-semibold">Reviews</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Link>
                    </>
            )}
        </Row>

        {/* Actions */}
        <Row md={2} lg={3} className="d-flex shadow-sm g-4 my-5 pb-4">
            <CustomButton link="/dashboard/manage-listings" value="Manage Listings" />
            <CustomButton link="/dashboard/manage-team" value="Manage Team"/>
            <CustomButton link="/dashboard/manage-reviews" value="Manage Reviews" />
        </Row>

        {/* Contact */}
        <Row className="dashboard-form shadow-sm g-4 my-5 pb-4 px-2 px-lg-3">
            <h4 className="fw-semibold mt-lg-5">CONTACT</h4>

            { editFeedback.success !== null && // Display alert when there's an update fail or success
                <Alert variant={ editFeedback.success ? "primary" : "danger" } className="text-center">{ editFeedback.message }</Alert>
            }

            {isLoading.contact ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" className="mx-auto" />
                    <p className="mt-2">Loading contact details...</p>
                </div>
            ) : (
                formState &&
                <Form onSubmit={ handleSubmit }>
                    <Row sm={2} xl={3}>
                        {/* Phone number */}
                        <Form.Group controlId="phone_number" className="mb-4">
                            <Form.Label>Phone number</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone_number"
                                value={ formState.phone_number }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group>

                        {/* Email */}
                        <Form.Group controlId="email" className="mb-4">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="text"
                                name="email"
                                value={ formState.email }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group>

                        {/* Youtube */}
                        <Form.Group controlId="youtube" className="mb-4">
                            <Form.Label>YouTube</Form.Label>
                            <Form.Control
                                type="text"
                                name="youtube"
                                value={ formState?.youtube || "" }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group>

                        {/* Instagram */}
                        <Form.Group controlId="instagram" className="mb-4">
                            <Form.Label>Instagram</Form.Label>
                            <Form.Control
                                type="text"
                                name="instagram"
                                value={ formState?.instagram || "" }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group>

                        {/* Facebook */}
                        <Form.Group controlId="facebook" className="mb-4">
                            <Form.Label>Facebook</Form.Label>
                            <Form.Control
                                type="text"
                                name="facebook"
                                value={ formState?.facebook || "" }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group>

                        {/* Twitter */}
                        <Form.Group controlId="twitter" className="mb-4">
                            <Form.Label>Twitter</Form.Label>
                            <Form.Control
                                type="text"
                                name="twitter"
                                value={ formState?.twitter || "" }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group>

                        {/* LinkedIn */}
                        {/* <Form.Group controlId="linkedin" className="mb-4">
                            <Form.Label>LinkedIn</Form.Label>
                            <Form.Control
                                type="text"
                                name="linkedin"
                                value={ formState?.linkedin || "" }
                                onChange={ handleChange }
                                disabled={ !editMode }
                                className="bg-white text-secondary"
                            />
                        </Form.Group> */}
                    </Row>

                    {/* Button */}
                    {getActionButton()}
                    <br></br>
                    {isLoading.contactFormAction === true &&
                        <div className="d-inline-block">
                            <Spinner animation="border" className="d-flex align-self-center mx-auto" variant="primary" />
                            <p className="mt-2 text-center">{ contact ? "Updating contact" : "Creating contact"}...</p>
                        </div>
                    }
                </Form>
            )}
        </Row>
        </>
    )
}

export default DashboardHome;
